import { CloseIcon } from '@chakra-ui/icons';
import { Box, Center, HStack, IconButton, Progress, VStack, useDisclosure } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Icon from 'components/Icon';
import { DiscordAttachModal } from 'components/Modal/DiscordAttachModal';
import { InviteModal } from 'components/Modal/InviteModal';
import Text from 'components/Text';
import { ERoute } from 'types/frontend';

import MissionItem from './MissionItem';
import { getAiMissionRoute, useOnboardingMissions } from './useOnboardingMissions';

const MAIN_PAGES: string[] = [
  ERoute.Dashboard,
  ERoute.Projects,
  ERoute.Challenges,
  ERoute.PracticeProblems,
  ERoute.Prototypes,
  ERoute.CodeFrames,
];

const DISMISSED_KEY_SUFFIX = '-onboarding-missions-dismissed';

const OnboardingMissions = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id: userId, isFree, isLegacy, userPreferences } = useCurrentUser();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [isDismissed, setIsDismissed] = useState(false);
  const { isOpen: isDiscordOpen, onOpen: onDiscordOpen, onClose: onDiscordClose } = useDisclosure();
  const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure();

  const { missions, completedCount, totalCount, allCompleted, isLoading } = useOnboardingMissions();

  // Check localStorage for dismissal on mount
  useEffect(() => {
    if (userId) {
      const dismissed = localStorage.getItem(`${userId}${DISMISSED_KEY_SUFFIX}`);
      if (dismissed) {
        setIsDismissed(true);
      }
    }
  }, [userId]);

  // Visibility: only on main listing pages
  const isMainPage = MAIN_PAGES.includes(pathname);

  // Visibility: only for legacy free users or paid users
  const isEligibleUser = !isFree || isLegacy;

  // Don't render if not eligible, not on main page, still loading, or dismissed (when all completed)
  if (!isMainPage || !isEligibleUser || !userId) {
    return null;
  }

  if (isDismissed && allCompleted) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(`${userId}${DISMISSED_KEY_SUFFIX}`, 'true');
    setIsDismissed(true);
    onClose();
  };

  const handleMissionAction = (missionId: string) => {
    if (missionId === 'connect-discord') {
      onDiscordOpen();
      return;
    }

    if (missionId === 'invite-friend') {
      onInviteOpen();
      return;
    }

    if (missionId === 'try-merlin') {
      navigate(getAiMissionRoute(userPreferences?.goal));
      return;
    }

    const mission = missions.find((m) => m.id === missionId);
    if (mission?.route) {
      navigate(mission.route);
    }
  };

  const progressPercent = (completedCount / totalCount) * 100;

  return (
    <>
      <Box position="fixed" bottom="24px" right="24px" zIndex={1000}>
        {/* Expanded panel */}
        {isOpen && (
          <Box
            mb={3}
            width="320px"
            bg="gray.800"
            borderRadius={12}
            border="1px solid"
            borderColor="whiteAlpha.200"
            boxShadow="0px 16px 40px rgba(0, 0, 0, 0.4)"
            overflow="hidden"
            sx={{
              '@keyframes panelSlideUp': {
                '0%': { opacity: 0, transform: 'translateY(8px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
              animation: 'panelSlideUp 0.25s ease-out forwards',
            }}
          >
            {/* Header */}
            <HStack px={4} pt={4} pb={2} justifyContent="space-between" alignItems="center">
              <HStack spacing={2}>
                <Icon type="Rocket" size={20} />
                <Text fontSize="medium" fontWeight="semibold">
                  Extra Missions
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="small" opacity="secondary">
                  {completedCount}/{totalCount}
                </Text>
                <IconButton
                  aria-label="Close missions"
                  icon={<CloseIcon boxSize={2.5} />}
                  size="xs"
                  variant="ghost"
                  onClick={onClose}
                />
              </HStack>
            </HStack>

            {/* Progress bar */}
            <Box px={4} pb={3}>
              <Progress
                value={progressPercent}
                size="xs"
                borderRadius={4}
                bg="whiteAlpha.200"
                sx={{
                  '& > div': {
                    background:
                      'linear-gradient(90deg, rgba(159, 122, 234, 0.87) 0%, rgba(237, 137, 54, 0.87) 100%)',
                  },
                }}
              />
            </Box>

            {/* Missions list */}
            {allCompleted ? (
              <VStack px={4} pb={4} spacing={3}>
                <Icon type="Rocket" size={32} />
                <Text fontSize="medium" fontWeight="semibold">
                  All missions complete!
                </Text>
                <Text fontSize="small" opacity="secondary" textAlign="center">
                  You&apos;ve explored all the key features. Keep building!
                </Text>
                <Box
                  as="button"
                  px={4}
                  py={1.5}
                  borderRadius={6}
                  bg="whiteAlpha.200"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  transition="all 0.15s ease-out"
                  onClick={handleDismiss}
                >
                  <Text fontSize="small">Dismiss</Text>
                </Box>
              </VStack>
            ) : (
              <VStack px={2} pb={3} spacing={0} alignItems="stretch">
                {missions.map((mission, index) => {
                  const isNextMission =
                    !mission.completed && missions.findIndex((m) => !m.completed) === index;

                  return (
                    <MissionItem
                      key={mission.id}
                      mission={mission}
                      isLegacy={isLegacy}
                      isNextMission={isNextMission}
                      onAction={handleMissionAction}
                    />
                  );
                })}
              </VStack>
            )}
          </Box>
        )}

        {/* Floating button */}
        <Box
          as="button"
          onClick={onToggle}
          position="relative"
          width="52px"
          height="52px"
          borderRadius="50%"
          background="linear-gradient(135deg, rgba(159, 122, 234, 0.9) 0%, rgba(237, 137, 54, 0.9) 100%)"
          boxShadow="0px 8px 24px rgba(0, 0, 0, 0.3)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          transition="all 0.2s ease-out"
          _hover={{ transform: 'scale(1.08)', boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.4)' }}
          ml="auto"
          sx={
            !allCompleted && !isOpen
              ? {
                  '@keyframes missionShake': {
                    '0%, 100%': { transform: 'rotate(0deg)' },
                    '15%': { transform: 'rotate(-6deg)' },
                    '30%': { transform: 'rotate(5deg)' },
                    '45%': { transform: 'rotate(-4deg)' },
                    '60%': { transform: 'rotate(2deg)' },
                    '75%': { transform: 'rotate(-1deg)' },
                  },
                  animation: 'missionShake 0.6s ease-in-out 1s 1',
                }
              : {}
          }
        >
          <Center width="100%" height="100%">
            <Icon type="Rocket" size={24} />
          </Center>
          {/* Progress badge */}
          {!allCompleted && (
            <Center
              position="absolute"
              top="-4px"
              right="-4px"
              minW="22px"
              height="22px"
              borderRadius="11px"
              bg="gray.800"
              border="2px solid"
              borderColor="purple.400"
              px={1}
            >
              <Text fontSize="xMicro" fontWeight="semibold">
                {completedCount}/{totalCount}
              </Text>
            </Center>
          )}
          {allCompleted && (
            <Center
              position="absolute"
              top="-4px"
              right="-4px"
              width="22px"
              height="22px"
              borderRadius="50%"
              bg="green.400"
            >
              <Box as="span" color="gray.900" fontSize="10px" fontWeight="bold" lineHeight="1">
                ✓
              </Box>
            </Center>
          )}
        </Box>
      </Box>
      <DiscordAttachModal isOpen={isDiscordOpen} onClose={onDiscordClose} />
      <InviteModal isOpen={isInviteOpen} onClose={onInviteClose} />
    </>
  );
};

export default OnboardingMissions;
